import {
  type ColumnDef,
  type ColumnFiltersState,
  type OnChangeFn,
  type SortingState,
} from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type RowSelectionData, useTable } from '@/hooks/useTable';
import { TableCell } from './TableCell';
import { TableHeader } from './TableHeader';
import { TablePaginationContext, type TablePaginationState } from './TablePaginationContext';
import { TableShell } from './TableShell';
import { CHECKBOX_FILTER_TYPES, type TablePagination } from './table.types';
import type { TableEntity } from './table-entity';
import { hasActiveColumnFilter } from './table-filters';
import type { TableStateController } from './useTableState';

interface TableProps<TData, TValue> {
  data?: TData[];
  columns: ColumnDef<TData, TValue>[];
  onRowClick?: (row: TData) => void;
  /** Filters/sorting/search state, persistence and reset — see useTableState. */
  tableState?: TableStateController;
  search?: string;
  columnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  initialSorting?: SortingState;
  rowSelection?: RowSelectionData;
  /** Keys the selection by a stable id instead of the row index. */
  getRowId?: (row: TData) => string;
  extraSearchableFields?: (row: TData) => Array<string | null | undefined>;
  footer?: React.ReactNode;
  rowClassName?: (row: TData) => string | undefined;
  onFilteredDataChange?: (data: TData[]) => void;
  /** Set on a table fed by an infinite query — see TablePagination for what it commits to. */
  pagination?: TablePagination;
  renderExpandedRow?: (row: TData) => React.ReactNode | null;
  disableHover?: boolean;
  fullscreenTable?: boolean;
  isLoading?: boolean;
  /** What the table lists — drives the empty, filtered and error messages. */
  entity: TableEntity;
  /** Set when a search or a non-default column filter is active. */
  isFiltered?: boolean;
  isError?: boolean;
  /** Escape hatch for the rare table that needs a richer empty state than a sentence. */
  emptyState?: React.ReactNode;
}

function findScrollParent(element: HTMLElement): HTMLElement {
  let current: HTMLElement | null = element.parentElement;
  while (current && current !== document.body) {
    const style = window.getComputedStyle(current);
    // An element with `overflow-x: auto` has its `overflow-y` computed as `auto` too (CSS spec),
    // even though it only scrolls horizontally and expands to its full content height vertically.
    // Only accept an ancestor that can actually scroll vertically, otherwise the virtualizer sees
    // a viewport as tall as the whole list, renders every row, and the infinite-scroll effect
    // fetches every page at once (Maximum update depth exceeded).
    const canScrollY = /(auto|scroll|overlay)/.test(style.overflowY);
    if (canScrollY && current.scrollHeight > current.clientHeight) {
      return current;
    }
    current = current.parentElement;
  }
  return (document.scrollingElement as HTMLElement | null) ?? document.documentElement;
}

export function Table<TData, TValue>(props: TableProps<TData, TValue>) {
  const {
    data,
    columns,
    onRowClick,
    tableState,
    rowSelection,
    getRowId,
    extraSearchableFields,
    footer,
    rowClassName,
    onFilteredDataChange,
    renderExpandedRow,
    disableHover,
    fullscreenTable = false,
    entity,
    isError,
    emptyState,
    pagination,
  } = props;

  const columnFilters = tableState?.columnFilters ?? props.columnFilters;
  const onColumnFiltersChange = tableState?.onColumnFiltersChange ?? props.onColumnFiltersChange;
  const sorting = tableState?.sorting ?? props.sorting;
  const onSortingChange = tableState?.onSortingChange ?? props.onSortingChange;
  const initialSorting = props.initialSorting;
  const search = props.search ?? (tableState?.searchInTable ? tableState.search : undefined);
  const isLoading = props.isLoading || (tableState ? !tableState.isLoaded : false);
  const isFiltered = props.isFiltered || (tableState ? tableState.search !== '' : false);

  // A `server` table never filters its slice: the rows it holds are already the server's answer.
  const manualFiltering = pagination?.filtering === 'server';
  useEffect(() => {
    if (pagination?.filtering === 'server') assertCheckboxFiltersDeclareOptions(columns);
  }, [pagination?.filtering, columns]);

  const defaultColumnSize = 150;
  const table = useTable({
    data: data ?? [],
    columns,
    search,
    columnFilters,
    onColumnFiltersChange,
    sorting,
    onSortingChange,
    initialSorting,
    rowSelection,
    getRowId,
    defaultColumnSize,
    extraSearchableFields,
    manualFiltering,
  });

  // `load-all`: a filter over a slice is a lie, so once a filter is active — or a checkbox filter
  // asked for its options — the remaining pages are pulled in until the list is whole.
  const [wantsAllRows, setWantsAllRows] = useState(false);
  const requestAllRows = useCallback(() => setWantsAllRows(true), []);
  const drainsPages =
    pagination?.filtering === 'load-all' && (wantsAllRows || hasActiveColumnFilter(columnFilters));
  const hasNextPage = pagination?.hasNextPage ?? false;
  const isFetchingNextPage = pagination?.isFetchingNextPage ?? false;
  const fetchNextPage = pagination?.fetchNextPage;
  useEffect(() => {
    if (drainsPages && hasNextPage && !isFetchingNextPage) fetchNextPage?.();
  }, [drainsPages, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const paginationState = useMemo<TablePaginationState | undefined>(
    () =>
      pagination && {
        filtering: pagination.filtering,
        isComplete: !hasNextPage && !isFetchingNextPage,
        requestAllRows,
      },
    [pagination, hasNextPage, isFetchingNextPage, requestAllRows]
  );

  const [containerNode, setContainerNode] = useState<HTMLDivElement | null>(null);
  const [scrollElement, setScrollElement] = useState<HTMLElement | null>(null);
  const [isScrolling, setIsScrolling] = useState(false);

  // Re-detect the scroll parent whenever the table changes height: while the list is loading,
  // empty or short (a remembered search) no ancestor is vertically scrollable yet, so an earlier
  // detection latched onto the document and the virtualizer never heard the real container scroll.
  const [containerHeight, setContainerHeight] = useState(0);
  useEffect(() => {
    if (!containerNode) return;
    const observer = new ResizeObserver(([entry]) =>
      setContainerHeight(Math.round(entry.contentRect.height))
    );
    observer.observe(containerNode);
    return () => observer.disconnect();
  }, [containerNode]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-run detection when the height changes
  useEffect(() => {
    if (!containerNode) {
      setScrollElement(null);
      return;
    }
    const scrollEl = fullscreenTable ? findScrollParent(containerNode) : containerNode;
    setScrollElement(scrollEl);

    const handleScroll = () => {
      setIsScrolling(scrollEl.scrollTop > 0);
    };

    scrollEl.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => scrollEl.removeEventListener('scroll', handleScroll);
  }, [containerNode, fullscreenTable, containerHeight]);

  // Push the filtered rows up, in display order (header totals, CSV exports), but only when the
  // *content* actually changes. The row model returns a fresh array reference on every render once
  // a filter is active, so an unconditional call here would setState → re-render → setState forever
  // ("Maximum update depth exceeded"). Comparing the mapped originals breaks that loop; row
  // originals stay referentially stable across renders (react-query structural sharing).
  const filteredRows = table.getSortedRowModel().rows;
  const lastEmittedRef = useRef<TData[] | null>(null);
  useEffect(() => {
    if (!onFilteredDataChange) return;
    const next = filteredRows.map((row) => row.original);
    const previous = lastEmittedRef.current;
    const isUnchanged =
      previous !== null &&
      previous.length === next.length &&
      previous.every((row, index) => row === next[index]);
    if (isUnchanged) return;
    lastEmittedRef.current = next;
    onFilteredDataChange(next);
  }, [filteredRows, onFilteredDataChange]);

  return (
    <TablePaginationContext.Provider value={paginationState}>
      <div
        ref={setContainerNode}
        className={`table-scroll relative rounded-lg border border-gray-200 text-xs ${fullscreenTable ? '' : 'overflow-x-auto overflow-y-auto'}`}
      >
        {isLoading && (
          <div
            role="progressbar"
            aria-label="Chargement..."
            aria-busy={true}
            className="absolute top-0 left-0 right-0 z-100 h-0.5 overflow-hidden rounded-t-lg"
          >
            <div
              className="absolute h-full w-1/3 bg-purple-600"
              style={{ animation: 'indeterminate 1.5s linear infinite' }}
            />
          </div>
        )}
        <TableShell
          table={table}
          header={(table) => (
            <TableHeader
              table={table}
              isScrolling={isScrolling}
              data={data ?? []}
              onResetFilters={tableState?.isDirty ? tableState.reset : undefined}
            />
          )}
          cell={(cellData, index) => <TableCell cell={cellData} index={index} />}
          onRowClick={onRowClick}
          footer={
            isFetchingNextPage ? (
              <tfoot>
                <tr>
                  <td
                    colSpan={99}
                    className="border-t border-gray-200 px-4 py-2 text-center text-xs text-gray-400"
                  >
                    Chargement...
                  </td>
                </tr>
              </tfoot>
            ) : (
              footer
            )
          }
          rowClassName={rowClassName}
          renderExpandedRow={renderExpandedRow}
          disableHover={disableHover}
          scrollElement={scrollElement}
          isLoading={isLoading}
          entity={entity}
          isFiltered={isFiltered}
          isError={isError}
          emptyState={emptyState}
          onFetchMore={hasNextPage ? fetchNextPage : undefined}
          isFetchingNextPage={isFetchingNextPage}
        />
      </div>
    </TablePaginationContext.Provider>
  );
}

// Thrown rather than logged: a paginated list offering the values of its first page as « all the
// values » is the kind of bug a client reports twice. The e2e suite opens every paginated page.
function assertCheckboxFiltersDeclareOptions<TData, TValue>(columns: ColumnDef<TData, TValue>[]) {
  if (!import.meta.env.DEV) return;
  const offenders = columns.filter(({ meta }) => {
    if (!meta?.filterType || !CHECKBOX_FILTER_TYPES.includes(meta.filterType)) return false;
    return !meta.filterOptions && !meta.useFilterOptions && !meta.options;
  });
  if (offenders.length === 0) return;
  const ids = offenders.map((column) => column.id ?? String(column.header)).join(', ');
  throw new Error(
    `Paginated table (server filtering): column(s) ${ids} would build their filter options from the rows loaded so far. Declare filterOptions / useFilterOptions, or use filterType 'search' or 'none'.`
  );
}
